import { z } from 'zod';
import { SubjectPrecondition } from './approval.ts';
import { Id, IdempotencyKey, IsoDateTime, JsonObject, Sha256Hex } from './common.ts';
import { ErrorCode } from './errors.ts';
import { HoldRequest } from './hold.ts';
import { BusinessOperationRef } from './operation.ts';

/** Autonomy classes: A0 inform, A1 prepare, A2 act within bounds, A3 approval-bound, A4 restricted. */
export const AutonomyClass = z.enum(['A0', 'A1', 'A2', 'A3', 'A4']).meta({ id: 'AutonomyClass' });
export type AutonomyClass = z.infer<typeof AutonomyClass>;

export const AUTONOMY_LABELS: Readonly<Record<AutonomyClass, string>> = {
  A0: 'inform',
  A1: 'prepare',
  A2: 'act within bounds',
  A3: 'approval-bound',
  A4: 'restricted',
};

/** Numeric rank for comparisons (`A0` = 0 … `A4` = 4). */
export function autonomyRank(value: AutonomyClass): number {
  return AutonomyClass.options.indexOf(value);
}

export const SideEffectClass = z
  .enum(['read', 'reversible_write', 'irreversible_write', 'external_communication'])
  .meta({ id: 'SideEffectClass' });
export type SideEffectClass = z.infer<typeof SideEffectClass>;

export const ActionTarget = z
  .object({ type: z.string().min(1), id: z.string().min(1), version: z.string().optional() })
  .meta({ id: 'ActionTarget' });
export type ActionTarget = z.infer<typeof ActionTarget>;

/** What must be true after the action; `expect` holds field values the verifier compares. */
export const Postcondition = z
  .object({ description: z.string().min(1), expect: JsonObject.default({}) })
  .meta({ id: 'Postcondition' });
export type Postcondition = z.infer<typeof Postcondition>;

/**
 * A proposed side effect. `argsHash = hashCanonical(args)`; the idempotency key for workflow-executed
 * actions is `${taskId}:${actionId}`.
 */
export const ActionIntent = z
  .object({
    actionId: Id,
    taskId: Id,
    tool: z.string().min(1),
    toolVersion: z.string().min(1),
    target: ActionTarget,
    args: JsonObject,
    argsHash: Sha256Hex,
    preconditions: z.array(z.string()).default([]),
    requestedScope: z.array(z.string()).default([]),
    idempotencyKey: IdempotencyKey,
    expectedPostcondition: Postcondition,
    autonomyClass: AutonomyClass,
    sideEffectClass: SideEffectClass,
    /** Short human title for UIs, e.g. "Create supplier ACME GmbH". */
    title: z.string().optional(),
    rationale: z.string().optional(),
    evidenceIds: z.array(z.string()).default([]),
    /**
     * Added by 17a: business subjects that must hold an `approved` approval request (exact type, id, revision,
     * hash) when the gateway executes this action. Optional without default so existing proposal hashes hold.
     */
    subjectPreconditions: z.array(SubjectPrecondition).optional(),
    /**
     * Added by 17a: the business operation this action performs, set by **pack code** from the tool's
     * `OperationKeyDeriver` (never by model output; the gateway re-derives and compares before claiming).
     */
    operation: BusinessOperationRef.optional(),
  })
  .meta({ id: 'ActionIntent' });
export type ActionIntent = z.infer<typeof ActionIntent>;

/** Exact effect preview shown to an approver. `targetVersion` is the target's current version. */
export const ChangePreview = z
  .object({
    actionId: Id,
    affectedRecords: z.array(
      z.object({
        type: z.string(),
        id: z.string(),
        before: JsonObject.nullable(),
        after: JsonObject.nullable(),
      }),
    ),
    externalRecipients: z.array(z.string()).default([]),
    consequence: z.string(),
    policyRefs: z.array(z.string()).default([]),
    evidenceIds: z.array(z.string()).default([]),
    targetVersion: z.string().optional(),
  })
  .meta({ id: 'ChangePreview' });
export type ChangePreview = z.infer<typeof ChangePreview>;

export const ReceiptStatus = z
  .enum(['succeeded', 'failed', 'unknown', 'denied'])
  .meta({ id: 'ReceiptStatus' });
export type ReceiptStatus = z.infer<typeof ReceiptStatus>;

export const ReceiptErrorClass = z
  .enum(['validation', 'policy', 'approval', 'timeout', 'downstream', 'unknown'])
  .meta({ id: 'ReceiptErrorClass' });
export type ReceiptErrorClass = z.infer<typeof ReceiptErrorClass>;

export const RetryHint = z.enum(['retry', 'reconcile', 'never']).meta({ id: 'RetryHint' });
export type RetryHint = z.infer<typeof RetryHint>;

/**
 * Added by 21 step 0 (brief 21 §1b.4): what a receipt records. `effect` (default when absent) is a forward tool
 * execution; `compensation` is the governed undo of an earlier receipt (`compensatesReceiptId`), run through the
 * compensation path with durable retry key `compensate:<receiptId>`.
 */
export const ReceiptKind = z.enum(['effect', 'compensation']).meta({ id: 'ReceiptKind' });
export type ReceiptKind = z.infer<typeof ReceiptKind>;

/** Result of one tool execution through the gateway. Completion is claimed only with `verifiedEffect`. */
export const ToolReceipt = z
  .object({
    receiptId: Id,
    actionId: Id,
    taskId: Id,
    status: ReceiptStatus,
    externalOperationId: z.string().optional(),
    verifiedEffect: JsonObject.optional(),
    resultingRecordVersion: z.string().optional(),
    startedAt: IsoDateTime,
    finishedAt: IsoDateTime,
    errorClass: ReceiptErrorClass.optional(),
    retryHint: RetryHint.optional(),
    output: z.unknown().optional(),
    /** Tool name, denormalised for display. */
    tool: z.string().optional(),
    /** Denial or failure reasons (policy reasons, validation messages). */
    reasons: z.array(z.string()).optional(),
    /** Added by 21 step 0: `compensation` for an undo receipt; absent = `effect`. */
    kind: ReceiptKind.optional(),
    /** Added by 21 step 0: the receipt a `compensation` receipt undoes. */
    compensatesReceiptId: Id.optional(),
  })
  .meta({ id: 'ToolReceipt' });
export type ToolReceipt = z.infer<typeof ToolReceipt>;

export const LedgerStatus = z
  .enum(['intended', 'executing', 'succeeded', 'failed', 'unknown', 'denied', 'reconciled'])
  .meta({ id: 'LedgerStatus' });
export type LedgerStatus = z.infer<typeof LedgerStatus>;

/**
 * Added by 21 step 0 (brief 21 §1b.10): ledger warning codes (the error taxonomy has no warnings). A warning never
 * blocks: `tool_scope_unset` — a process activation whose pinned definition declares no `tools` called a tool; the
 * gateway kept the pre-21 behaviour and recorded the warning.
 */
export const LedgerWarningCode = z.enum(['tool_scope_unset']).meta({ id: 'LedgerWarningCode' });
export type LedgerWarningCode = z.infer<typeof LedgerWarningCode>;

/** One action in the action ledger (unique by `idempotencyKey`). */
export const LedgerEntry = z
  .object({
    actionId: Id,
    taskId: Id,
    tool: z.string(),
    toolVersion: z.string(),
    target: ActionTarget,
    argsHash: Sha256Hex,
    idempotencyKey: IdempotencyKey,
    status: LedgerStatus,
    approvalId: Id.optional(),
    externalOperationId: z.string().optional(),
    receipt: ToolReceipt.optional(),
    attempts: z.number().int().nonnegative(),
    lastError: z.object({ code: ErrorCode, message: z.string() }).optional(),
    /** Added by 21 step 0: non-blocking warnings recorded with the entry. */
    warnings: z.array(LedgerWarningCode).optional(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'LedgerEntry' });
export type LedgerEntry = z.infer<typeof LedgerEntry>;

/**
 * Added by 17c1 (D7, ADR-017 amendment): ledger entries filtered by the side-effect class of their tool. The action
 * ledger records **every** governed tool call, reads included (`sideEffectClass: 'read'` entries document what was
 * read and are never claimed as business operations), so "the task wrote nothing" is
 * `ledgerEntriesByEffect(entries, classOf, 'write').length === 0`, not "the ledger is empty".
 * `effects: 'none'` keeps the reads, `'write'` keeps every other class; a tool whose class is unknown
 * (`classOf` returns undefined) counts as a write (fail closed).
 */
export function ledgerEntriesByEffect<E extends Pick<LedgerEntry, 'tool'>>(
  entries: readonly E[],
  classOf: (tool: string) => SideEffectClass | undefined,
  effects: 'none' | 'write',
): E[] {
  return entries.filter((e) => (classOf(e.tool) === 'read') === (effects === 'none'));
}

/**
 * Added 2026-11-03 (own-write drift): one succeeded write of this task, as `explainTargetVersion` reads it (a
 * workflow `Executed` receipt or an action-ledger entry). `at` is when it ran (receipt `finishedAt`, ledger
 * `createdAt`).
 */
export interface OwnTargetWrite {
  actionId: string;
  target: Pick<ActionTarget, 'type' | 'id'>;
  status: ReceiptStatus;
  kind?: ReceiptKind | undefined;
  resultingRecordVersion?: string | undefined;
  at: string;
}

/** Result of `explainTargetVersion`: the version to validate the approval against, and the writes that explain it. */
export interface TargetVersionExplanation {
  version: string;
  explainedBy: string[];
}

const RECORD_VERSION_INT = /^(0|[1-9]\d*)$/;

/**
 * Added 2026-11-03 (own-write drift): an approval pins `targetVersions[actionId]` at request time and binds the whole
 * proposal (`proposalHash`), so earlier actions of the same proposal may move the target before the approved one runs.
 * Returns `approvedVersion` (drift explained) when this task's own succeeded effect writes on `target`, run at or
 * after `since` (the approval's `requestedAt`) and given in execution order, account for the change from
 * `approvedVersion` to `currentVersion`; otherwise returns `currentVersion` unchanged so `target_version_changed`
 * still fires.
 * - Integer versions: the writes' `resultingRecordVersion`s must be exactly `approved+1, approved+2, …, current`.
 *   A gap means someone else wrote in between.
 * - Other versions (etags, hashes): accepted when the last own write produced `currentVersion`. Weaker: a foreign
 *   write before the first own write cannot be seen.
 * Compensations, non-succeeded receipts, writes without a version and an empty `approvedVersion` explain nothing.
 */
export function explainTargetVersion(input: {
  target: Pick<ActionTarget, 'type' | 'id'>;
  approvedVersion: string;
  currentVersion: string;
  ownWrites: readonly OwnTargetWrite[];
  since: string;
}): TargetVersionExplanation {
  const { approvedVersion, currentVersion, target } = input;
  const unexplained: TargetVersionExplanation = { version: currentVersion, explainedBy: [] };
  if (currentVersion === approvedVersion) return { version: approvedVersion, explainedBy: [] };
  if (approvedVersion === '') return unexplained;
  const since = Date.parse(input.since);
  if (Number.isNaN(since)) return unexplained;
  const writes = input.ownWrites.filter(
    (w) =>
      w.target.type === target.type &&
      w.target.id === target.id &&
      w.status === 'succeeded' &&
      w.kind !== 'compensation' &&
      Date.parse(w.at) >= since,
  );
  if (writes.length === 0) return unexplained;
  const versions = writes.map((w) => w.resultingRecordVersion);
  if (versions.some((v) => v === undefined || v === '')) return unexplained;
  const chain = versions as string[];
  const explained = { version: approvedVersion, explainedBy: writes.map((w) => w.actionId) };
  const numeric = [approvedVersion, currentVersion, ...chain].every((v) => RECORD_VERSION_INT.test(v));
  if (numeric) {
    const start = BigInt(approvedVersion);
    const contiguous = chain.every((v, i) => BigInt(v) === start + BigInt(i + 1));
    return contiguous && chain[chain.length - 1] === currentVersion ? explained : unexplained;
  }
  return chain[chain.length - 1] === currentVersion ? explained : unexplained;
}

// -----------------------------------------------------------------------------------------------
// Recovery (added by 21 step 0, brief 21 §1, §1b.4; ADR-021)
// -----------------------------------------------------------------------------------------------

/** Where a `recover` handler sends the process: retry the activation, rerun an earlier one, hold, or fail. */
export const RecoveryTarget = z.enum(['retry', 'rerun', 'hold', 'fail']).meta({ id: 'RecoveryTarget' });
export type RecoveryTarget = z.infer<typeof RecoveryTarget>;

/** Per completed receipt in scope: let the effect stand, or compensate it through the governed path. */
export const EffectDecision = z
  .object({ receiptId: Id, decision: z.enum(['stand', 'compensate']) })
  .meta({ id: 'EffectDecision' });
export type EffectDecision = z.infer<typeof EffectDecision>;

/**
 * Returned by a `recover` handler (and given to `cancelChild`). Returning to a step never undoes an effect
 * silently: `effects` has exactly one entry per completed receipt in scope (a missing or unknown receipt is
 * `recovery_effects_incomplete`, thrown). `compensate` on a receipt whose tool has no `compensate` is **not**
 * thrown: the entry becomes `stand` plus an open `manual_reversal` obligation (owner role from the activity) and code
 * `compensation_unavailable` is recorded on the timeline and ledger (op-08). `resume` after `recover` blocks until
 * every `compensate` is settled.
 */
export const RecoveryDecision = z
  .object({
    target: RecoveryTarget,
    effects: z.array(EffectDecision),
    /** `rerun`: the activation to re-enter (a new iteration of it). */
    rerunActivationId: z.string().min(1).max(200).optional(),
    /** `hold`: the hold to open. */
    hold: HoldRequest.optional(),
    /** `fail`: the reason recorded on the instance. */
    reason: z.string().min(1).max(2000).optional(),
  })
  .superRefine((d, ctx) => {
    if (d.target === 'rerun' && d.rerunActivationId === undefined) {
      ctx.addIssue({ code: 'custom', message: 'rerun needs rerunActivationId', path: ['rerunActivationId'] });
    }
    if (d.target === 'hold' && d.hold === undefined) {
      ctx.addIssue({ code: 'custom', message: 'hold needs hold', path: ['hold'] });
    }
  })
  .meta({ id: 'RecoveryDecision' });
export type RecoveryDecision = z.infer<typeof RecoveryDecision>;
export type RecoveryDecisionInput = z.input<typeof RecoveryDecision>;

/**
 * Receipts in scope that `effects` misses, and entries naming a receipt not in scope or named twice. Both lists
 * empty = complete; otherwise the caller throws `recovery_effects_incomplete` with these details.
 */
export function recoveryEffectsIssues(
  effects: readonly EffectDecision[],
  receiptIdsInScope: readonly string[],
): { missing: string[]; unexpected: string[] } {
  const inScope = new Set(receiptIdsInScope);
  const seen = new Set<string>();
  const unexpected: string[] = [];
  for (const e of effects) {
    if (!inScope.has(e.receiptId) || seen.has(e.receiptId)) unexpected.push(e.receiptId);
    seen.add(e.receiptId);
  }
  const missing = receiptIdsInScope.filter((id) => !seen.has(id));
  return { missing, unexpected };
}

/** Durable retry key of the compensation of one receipt (§1b.4). */
export function compensationKey(receiptId: string): string {
  return `compensate:${receiptId}`;
}

/**
 * Added by 21a (brief 21 §1b.4, op-06..op-11): what happened to one per-receipt entry of an enforced recovery
 * decision (`recover` or `cancelChild`). `stand` and `settled` are done; `pending`, `unknown` (the compensation's
 * ledger entry is `unknown`, awaiting the ledger reconcile sweep) and `failed` (retried under the same key
 * `compensate:<receiptId>` when the recovery is settled again) keep the recovery `unsettled`, and `resume` refuses.
 */
export const RecoveryEffectStatus = z
  .enum(['stand', 'pending', 'unknown', 'settled', 'failed'])
  .meta({ id: 'RecoveryEffectStatus' });
export type RecoveryEffectStatus = z.infer<typeof RecoveryEffectStatus>;

/** Added by 21a: one entry of an enforced recovery decision with its settlement. */
export const RecoveryEffectOutcome = z
  .object({
    receiptId: Id,
    decision: z.enum(['stand', 'compensate']),
    status: RecoveryEffectStatus,
    /** `compensate` asked on a tool without `compensate`: recorded as `stand` (`compensation_unavailable`, op-08). */
    unavailable: z.boolean().optional(),
    /** The tool and action of the receipt (denormalised; the compensation maps the action's stored intent). */
    tool: z.string().optional(),
    actionId: Id.optional(),
    /** The compensation's receipt (`kind: 'compensation'`, `compensatesReceiptId`) once it exists. */
    compensationReceiptId: Id.optional(),
    /** `unavailable`: the open `manual_reversal` obligation (absent while no `ObligationStore` is bound). */
    obligationId: Id.optional(),
    /** `unavailable`: a manual reversal is owed (an obligation, or recorded on the timeline while none is bound). */
    manualReversal: z.boolean().optional(),
    /** Last compensation failure (`failed` / `unknown`). */
    lastError: z.string().max(2000).optional(),
  })
  .meta({ id: 'RecoveryEffectOutcome' });
export type RecoveryEffectOutcome = z.infer<typeof RecoveryEffectOutcome>;

/** Added by 21a: an enforced recovery decision of an instance (the `recoveries` row as the orchestrator reports it). */
export const RecoveryOutcome = z
  .object({
    /** `recover:<processInstanceId>:<name>#<iteration>` or `cancel-child:<processInstanceId>:<activationId>`. */
    recoveryId: z.string().min(1),
    processInstanceId: Id,
    /** Scope name (`recover`) or `cancel-child`. */
    name: z.string().min(1),
    activationId: z.string().min(1),
    decision: RecoveryDecision,
    effects: z.array(RecoveryEffectOutcome),
    state: z.enum(['settled', 'unsettled']),
  })
  .meta({ id: 'RecoveryOutcome' });
export type RecoveryOutcome = z.infer<typeof RecoveryOutcome>;

/** Added by 21a: the deterministic id of the recovery of iteration `iteration` of scope `name` (§1b.4). */
export function recoveryIdOf(processInstanceId: string, name: string, iteration: number): string {
  return `recover:${processInstanceId}:${name}#${iteration}`;
}
